import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Check,
  ExternalLink,
  AlertTriangle,
  Sparkles,
  Zap,
  CreditCard,
  LogOut
} from 'lucide-react';
import { SettingsSection } from './SettingsSection';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import type { AppSettings, LLMProvider } from '../../../shared/types/settings';

// Provider info from IPC (matches ProviderInfo in provider-handlers.ts)
interface ProviderInfo {
  id: LLMProvider;
  name: string;
  description: string;
  authenticated: boolean;
  authType: 'oauth' | 'api_key' | 'cli';
  models: string[];
  accountId?: string;
}

interface ProviderSettingsProps {
  settings: AppSettings;
  onSettingsChange: (settings: AppSettings) => void;
}

// Fallback provider data (shown while loading from IPC)
const FALLBACK_PROVIDERS: ProviderInfo[] = [
  {
    id: 'claude',
    name: 'Claude',
    description: 'Anthropic Claude via Claude Code CLI subscription',
    authenticated: true,
    authType: 'cli',
    models: ['claude-sonnet-4-5-20250929', 'claude-opus-4-5-20251101', 'claude-haiku-4-5-20251001'],
  },
  {
    id: 'codex',
    name: 'OpenAI Codex',
    description: 'Use your ChatGPT Pro/Plus subscription',
    authenticated: false,
    authType: 'oauth',
    models: ['gpt-5.2-codex', 'gpt-5.1-codex-max', 'gpt-5.1-codex', 'gpt-5.1-codex-mini'],
  },
  {
    id: 'openai_api',
    name: 'OpenAI API',
    description: 'Direct OpenAI API with your own API key',
    authenticated: false,
    authType: 'api_key',
    models: ['gpt-5.2', 'gpt-5.1', 'gpt-4o', 'gpt-4o-mini', 'o1', 'o1-mini'],
  },
];

/**
 * Provider Settings Component
 * Allows users to select between Claude, Codex, and OpenAI API providers
 */
export function ProviderSettings({ settings, onSettingsChange }: ProviderSettingsProps) {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  
  const [providers, setProviders] = useState<ProviderInfo[]>(FALLBACK_PROVIDERS);
  const [currentProvider, setCurrentProvider] = useState<LLMProvider>(settings.llmProvider || 'claude');
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Fetch providers from IPC
  const fetchProviders = useCallback(async () => {
    console.log('[ProviderSettings] Fetching available providers...');
    try {
      const result = await window.electronAPI.provider.getAvailable();
      console.log('[ProviderSettings] Fetched providers:', result.data);
      if (result.success && result.data) {
        setProviders(result.data);
      }
    } catch (error) {
      console.error('[ProviderSettings] Error fetching providers:', error);
    } finally {
      setIsFetching(false);
    }
  }, []);

  // Fetch current provider from IPC
  const fetchCurrentProvider = useCallback(async () => {
    try {
      const result = await window.electronAPI.provider.getCurrent();
      if (result.success && result.data) {
        setCurrentProvider(result.data.provider);
      }
    } catch (error) {
      console.error('[ProviderSettings] Error fetching current provider:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProviders();
    fetchCurrentProvider();
  }, [fetchProviders, fetchCurrentProvider]);

  // Listen for auth changes
  useEffect(() => {
    const cleanup = window.electronAPI.provider.onAuthChanged((data) => {
      console.log('[ProviderSettings] Auth changed:', data);
      // Refresh providers list to update auth status
      fetchProviders();
      
      if (data.authenticated) {
        toast({
          title: t('provider.toast.authSuccess'),
          description: t('provider.toast.authSuccessDescription', { provider: 'ChatGPT' }),
        });
      }
    });

    return cleanup;
  }, [fetchProviders, t, toast]);

  const handleProviderChange = async (provider: LLMProvider) => {
    console.log('[ProviderSettings] handleProviderChange called:', provider);
    const providerInfo = providers.find(p => p.id === provider);
    console.log('[ProviderSettings] Provider info:', providerInfo);
    
    // Show informational toast if provider needs authentication
    // But DON'T block the selection - let user switch so auth UI appears
    const needsAuth = !providerInfo?.authenticated && provider !== 'claude';
    if (needsAuth) {
      console.log('[ProviderSettings] Provider needs authentication, showing auth UI');
      toast({
        title: t('provider.toast.authRequired'),
        description: t('provider.toast.authRequiredDescription'),
      });
      // Continue to switch provider so auth section appears
    }

    setIsLoading(true);
    try {
      console.log('[ProviderSettings] Calling setActive for provider:', provider);
      const result = await window.electronAPI.provider.setActive(provider);
      console.log('[ProviderSettings] setActive result:', result);
      
      if (result.success) {
        setCurrentProvider(provider);
        console.log('[ProviderSettings] Provider switched to:', provider);
        
        // Update local settings
        onSettingsChange({
          ...settings,
          llmProvider: provider,
        } as AppSettings);

        // Only show success toast if already authenticated
        if (!needsAuth) {
          toast({
            title: t('provider.toast.switchSuccess'),
            description: t('provider.toast.switchSuccessDescription', { provider: providerInfo?.name }),
          });
        }
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('[ProviderSettings] Error switching provider:', error);
      toast({
        title: t('provider.toast.switchFailed'),
        description: t('provider.toast.switchFailedDescription', { error: String(error) }),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodexAuthenticate = async () => {
    console.log('[ProviderSettings] Starting Codex OAuth authentication...');
    setIsAuthenticating(true);
    try {
      const result = await window.electronAPI.provider.codex.startAuth();
      console.log('[ProviderSettings] Codex auth result:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Authentication failed');
      }
      
      console.log('[ProviderSettings] Codex OAuth flow started, waiting for callback...');
      // Auth success will be handled by the onAuthChanged listener
      // The browser will open for OAuth flow
    } catch (error) {
      console.error('[ProviderSettings] Codex auth error:', error);
      toast({
        title: t('provider.toast.authFailed'),
        description: t('provider.toast.authFailedDescription', { error: String(error) }),
        variant: 'destructive',
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleCodexLogout = async () => {
    try {
      const result = await window.electronAPI.provider.codex.logout();
      
      if (result.success) {
        // Refresh providers to update auth status
        await fetchProviders();
        
        toast({
          title: t('provider.toast.logoutSuccess'),
          description: t('provider.toast.logoutSuccessDescription', { provider: 'ChatGPT Codex' }),
        });
      }
    } catch (error) {
      console.error('[ProviderSettings] Logout error:', error);
    }
  };

  const getProviderIcon = (id: LLMProvider) => {
    switch (id) {
      case 'claude':
        return <Sparkles className="h-5 w-5 text-purple-500" />;
      case 'codex':
        return <Zap className="h-5 w-5 text-green-500" />;
      case 'openai_api':
        return <CreditCard className="h-5 w-5 text-blue-500" />;
    }
  };

  const getProviderTranslationKey = (id: LLMProvider): string => {
    switch (id) {
      case 'claude':
        return 'claude';
      case 'codex':
        return 'codex';
      case 'openai_api':
        return 'openaiApi';
    }
  };

  const codexProvider = providers.find(p => p.id === 'codex');
  const isCodexAuthenticated = codexProvider?.authenticated ?? false;

  return (
    <div className="space-y-6">
      <SettingsSection
        title={t('provider.title')}
        description={t('provider.description')}
      >
        {/* Provider Selection Cards */}
        <div className="space-y-4">
          <Label className="text-sm font-medium text-foreground">
            {t('provider.selectProvider')}
          </Label>

          {isFetching ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading providers...
            </div>
          ) : (
            <div className="grid gap-3">
              {providers.map((provider) => {
                const isSelected = currentProvider === provider.id;
                const isDisabled = isLoading || (isAuthenticating && provider.id === 'codex');
                const translationKey = getProviderTranslationKey(provider.id);

                return (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderChange(provider.id)}
                    disabled={isDisabled}
                    className={cn(
                      'w-full p-4 rounded-lg border text-left transition-all',
                      'hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-primary/50',
                      isDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <div className="flex items-start gap-4">
                      <div className="mt-0.5">
                        {getProviderIcon(provider.id)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">
                            {t(`provider.providers.${translationKey}.name`)}
                          </span>
                          {provider.id === 'claude' && (
                            <Badge variant="secondary" className="text-xs">
                              {t('provider.providers.claude.recommended')}
                            </Badge>
                          )}
                          {isSelected && (
                            <Check className="h-4 w-4 text-primary ml-auto" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t(`provider.providers.${translationKey}.description`)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t(`provider.providers.${translationKey}.features`)}
                        </p>
                        
                        {/* Authentication Status */}
                        <div className="flex items-center gap-2 mt-2">
                          {provider.authenticated ? (
                            <Badge variant="outline" className="text-xs text-green-600 border-green-600/30 bg-green-600/10">
                              <Check className="h-3 w-3 mr-1" />
                              {t('provider.status.authenticated')}
                              {provider.accountId && (
                                <span className="ml-1 opacity-75">({provider.accountId.slice(0, 8)}...)</span>
                              )}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-600/30 bg-amber-600/10">
                              {t('provider.status.notAuthenticated')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Codex Authentication Section */}
      {currentProvider === 'codex' && !isCodexAuthenticated && (
        <SettingsSection
          title={t('provider.codexAuth.title')}
          description={t('provider.codexAuth.description')}
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('provider.codexAuth.instructions')}
            </p>
            
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
              <p className="text-sm text-amber-600 dark:text-amber-400">
                {t('provider.codexAuth.browserNote')}
              </p>
            </div>

            <Button
              onClick={handleCodexAuthenticate}
              disabled={isAuthenticating}
              className="w-full sm:w-auto"
            >
              {isAuthenticating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('provider.actions.authenticating')}
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('provider.actions.authenticate')}
                </>
              )}
            </Button>
          </div>
        </SettingsSection>
      )}

      {/* Codex Logout Section (when authenticated) */}
      {currentProvider === 'codex' && isCodexAuthenticated && (
        <SettingsSection
          title={t('provider.codexAuth.title')}
          description={t('provider.codexAuth.authenticatedDescription')}
        >
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="text-green-600 border-green-600/30 bg-green-600/10">
              <Check className="h-3 w-3 mr-1" />
              {t('provider.status.authenticated')}
              {codexProvider?.accountId && (
                <span className="ml-1 opacity-75">({codexProvider.accountId.slice(0, 8)}...)</span>
              )}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCodexLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('provider.actions.logout')}
            </Button>
          </div>
        </SettingsSection>
      )}

      {/* Model Selection */}
      <SettingsSection
        title={t('provider.models.title')}
        description={t('provider.models.currentModel')}
      >
        <div className="space-y-3">
          <Label htmlFor="model-select" className="text-sm font-medium text-foreground">
            {t('provider.models.selectModel')}
          </Label>
          <Select
            value={settings.llmModel || providers.find(p => p.id === currentProvider)?.models[0]}
            onValueChange={(value) => onSettingsChange({
              ...settings,
              llmModel: value,
            } as AppSettings)}
          >
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder={t('provider.models.selectModel')} />
            </SelectTrigger>
            <SelectContent>
              {providers.find(p => p.id === currentProvider)?.models.map((model) => (
                <SelectItem key={model} value={model}>
                  {model}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </SettingsSection>

      {/* Warnings */}
      {currentProvider === 'codex' && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-muted border border-border">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              {t('provider.warnings.codexLimitations')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('provider.warnings.switchingProviders')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
